import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Paging,
  Pager,
  SearchPanel,
  Scrolling,
} from 'devextreme-react/data-grid';
import CustomStore from 'devextreme/data/custom_store';
import { Card } from 'react-bootstrap';
import { Button } from 'devextreme-react/button';
import Loader from 'Common/Loader';
import {
  DataGrid,
  Column,
  DataGrid as DxDataGridType,
} from 'devextreme-react/data-grid';
import type DataGridInstance from 'devextreme/ui/data_grid';
import { GetDailyReport } from '../services/DailyReportAPI';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from 'react-router-dom';
import notistack from 'modules/core/notistack';
import { GetMaterialTypeList } from 'modules/transactions/estimate-payment-receipt/services';

interface DailyReport {
  id: number;
  materialType: string;
  categoryName: string;
  pcsCount: number;
  totalWeight: number;
}


const DailyReportList: React.FC = () => {
  const dataGridRef = useRef<DxDataGridType>(null);
  const [selectedMaterialTypeId, setSelectedMaterialTypeId] = useState('All');
  const [reportData, setReportData] = useState<DailyReport[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totals, setTotals] = useState({
    gold: 0,
    diamond: 0,
    goldCoin: 0,
    silverCoin: 0,
  });

  const dataSource = useMemo(() => {
    return new CustomStore<DailyReport, number>({
      key: 'id',
      load: async (loadOptions) => {
        try {
          setIsLoading(true);

          const params = new URLSearchParams();

          if (selectedMaterialTypeId !== 'All') {
            params.append('materialTypeId', selectedMaterialTypeId);
          }

          if (loadOptions.skip !== undefined) {
            params.append('skip', loadOptions.skip.toString());
          }
          if (loadOptions.take !== undefined) {
            params.append('take', loadOptions.take.toString());
          }

          const response = await GetDailyReport(params);
          const result = response?.data ?? [];

          setReportData(result);

          // Compute totals
          const counts = {
            gold: result.filter(
              (x: DailyReport) => x.materialType?.toLowerCase() === 'gold'
            ).length,
            diamond: result.filter(
              (x: DailyReport) => x.materialType?.toLowerCase() === 'diamond'
            ).length,
            goldCoin: result.filter(
              (x: DailyReport) => x.materialType?.toLowerCase() === 'gold coin'
            ).length,
            silverCoin: result.filter(
              (x: DailyReport) => x.materialType?.toLowerCase() === 'silver coin'
            ).length,
          };
          setTotals(counts);

          return {
            data: result,
            totalCount: result.length,
          };
        } catch (error) {
          console.error('Failed to load report:', error);
          return { data: [], totalCount: 0 };
        } finally {
          setIsLoading(false);
        }
      },
    });
  }, [selectedMaterialTypeId]);

  const renderSerialNumber = (cellData: any) => {
    const rowIndex =
      cellData.rowIndex +
      1 +
      (cellData.component.pageIndex() ?? 0) * (cellData.component.pageSize() ?? 10);
    return <span>{rowIndex}</span>;
  };

  const handleExportExcel = () => {
    if (!reportData || reportData.length === 0) return;

    const sanitizedData = reportData.map(({ id, ...rest }) => rest);

    const worksheet = XLSX.utils.json_to_sheet(sanitizedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Report');

    const excelBuffer = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    const blob = new Blob([excelBuffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    saveAs(blob, 'Daily_Report_Export.xlsx');
  };

  const handlePrint = () => {
    if (!reportData || reportData.length === 0) return;

    // Remove 'id' field
    const sanitizedData = reportData.map(({ id, ...rest }) => rest);
    console.log(sanitizedData)
    // Create a new jsPDF instance
    const doc = new jsPDF() as any;

    // Prepare table columns
    const columns = [
      { header: "Material Type", dataKey: "materialType" },
      { header: "Category", dataKey: "categoryName" },
      { header: "Quantity", dataKey: "pcsCount" },
      { header: "Total Weight", dataKey: "totalWeight" }
    ];

    autoTable(doc, {
      columns,
      body: sanitizedData,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [200, 200, 200] },
      startY: 20,
      margin: { left: 10, right: 10 }
    });

    doc.save("Daily_Report_Export.pdf");
  };
  const navigate = useNavigate()
  const [materialTypes, setMaterialTypes] = useState<{ value: string; text: string }[]>([]);

  const MaterialTypeListData = async () => {
    try {
      const response = await GetMaterialTypeList()
      setMaterialTypes(response)
    } catch (error: any) {
      console.log(error.message)
      notistack.notifyFromApi(error.message)
      setMaterialTypes([])
    }
  }

  const handleNavigate = () => {
    const selectedMaterial = materialTypes.find(
      (mt) => mt.value === selectedMaterialTypeId
    );

    if (!selectedMaterial) {
      return;
    }

    // Convert text to lowercase and replace spaces if needed
    const route = `/${selectedMaterial.text.toLowerCase()}-product-inventory`;
    navigate(route);
  };

  useEffect(() => {
    MaterialTypeListData()
  }, [])

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <h5 className="m-0">Daily Report</h5>
              {selectedMaterialTypeId?.trim() !== "" && (
                <button onClick={handleNavigate} className="btn add-btn btn-sm">
                  View Details
                </button>
              )}
            </Card.Header>

            <div className=" d-flex align-items-center justify-content-between gap-3 mt-1 p-2  flex-wrap">
              <div className="d-flex flex-column">
                <label htmlFor="mtType" className="mb-1">
                  Material Type
                </label>
                <select
                  id="mtType"
                  className="form-select form-select-sm w-auto"
                  value={selectedMaterialTypeId}
                  onChange={(e) => setSelectedMaterialTypeId(e.target.value)}
                >
                  <option value="">All</option>
                  {materialTypes?.length > 0 &&
                    materialTypes
                      .filter((option) =>
                        ["gold", "diamond"].includes(option.text?.toLowerCase())
                      )
                      .map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.text}
                        </option>
                      ))}

                </select>
              </div>

              <div className="d-flex flex-wrap gap-3 ms-3">
                <span className="fw-semibold">
                  Total Gold Products:{' '}
                  <span>{totals.gold}</span>
                </span>
                <span className="fw-semibold">
                  Total Diamond Products:{' '}
                  <span >{totals.diamond}</span>
                </span>
              </div>
              <div className="d-flex justify-content-end gap-2 ">
                <Button
                  className='btn'
                  icon="https://img.favpng.com/18/7/13/scalable-vector-graphics-microsoft-excel-clip-art-portable-network-graphics-png-favpng-yynmZdMbFF2A9c7tcbZa6Ewv5.jpg"
                  disabled={isLoading || reportData.length === 0}
                  hint="Export to Excel"
                  onClick={handleExportExcel}
                />
                <button
                  className="btn btn-sm submit-btn"
                  onClick={handlePrint}
                  disabled={isLoading || reportData.length === 0}
                >
                  Print
                </button>
              </div>
            </div>

            <Card.Body className='p-0 m-0 w-100'>
              {isLoading && <Loader />}
              <DataGrid
                ref={dataGridRef}
                dataSource={dataSource}
                showRowLines
                keyExpr="id"
                showBorders
                columnAutoWidth={true}
                remoteOperations={false}
                onContentReady={(e) => {
                  const hasData = e.component.totalCount() > 0;
                  e.component.option("pager.visible", hasData);
                  e.component.option("paging.visible", hasData);
                }}
              >
                <Pager
                  showPageSizeSelector
                  allowedPageSizes={[10, 25, 50, 100]}
                  showNavigationButtons
                  displayMode="full"
                  showInfo
                />
                <Paging defaultPageSize={10} />
                <SearchPanel visible width={240} placeholder="Search..." />
                <Scrolling mode="virtual" />

                <Column
                  caption="Sr.No."
                  dataType="number"
                  alignment="center"
                  cellRender={renderSerialNumber}
                  width={70}
                />
                <Column
                  dataField="materialType"
                  caption="Material Type"
                  dataType="string"
                  width={150}
                />
                <Column
                  width={200}
                  dataField="categoryName"
                  caption="Category Name"
                  dataType="string"
                />
                <Column
                  width={100}
                  dataField="pcsCount"
                  cssClass='text-start'
                  caption="Quantity"
                  dataType="number"
                />
                <Column
                  dataField="totalWeight"
                  cssClass='text-start'
                  caption="Total Weight"
                  dataType="number"
                  format="#,##0.000"
                />
              </DataGrid>
            </Card.Body>
          </Card>
        </div>
      </div>
    </React.Fragment>
  );
};

export default DailyReportList;